<div
  stchPopover
  [stchPopoverArrow]="true"
  [stchPopoverContent]="popoverTemplate"
  [stchPopoverInnerShadow]="true"
  stchPopoverPlacement="right"
  [stchPopoverDisabled]="!minimized"
  stchPopoverTrigger="click"
  >
  <!--
    `tenantId`, NOT `team?.id` (#1147 ROW 2). `currentTeamId` is whatever the last click stored, and
    this row's own click emits a filter the host answers by setting the team to THIS tree's tenant —
    the member mailbox on the My Workspace arm, the team mailbox on the team's. `team` is `undefined`
    on the main rail, because side-bar-teams renders the host template with an undefined team, so the
    old comparison was `'<member-id>' === undefined` and My Workspace's row could never be drawn
    selected while the team's always could. `tenantId` is already exactly the id each arm's click
    stores — `side-bar.component.ts:172` builds the team mailbox with the id of
    `currentTeamWorkspace`, the same id this tree is given — so this reads the right tenant on BOTH
    arms while `team` goes on owning the persisted expand key and the tree's input.

    THE THIRD ARM, and why the fallback (#1147 review F1). This template has THREE hosts and only two
    of them bind `[tenantId]`: the private/flat arm (`side-bar.component.html:32`) binds `[team]`
    alone, and that arm is rendered once per legacy team through `stch-side-bar-teams`'s
    `{ team: <Team> }` context. Reading `tenantId` there reads `undefined` for ever, so a nested
    team's own `Notebooks` row stopped filling when you were in it - the mirror image of the defect
    ROW 2 came to fix. `?? team?.id` is INERT on the two arms that bind a real id, and on the one that
    does not it falls through to exactly the pre-round comparison. Binding `[tenantId]="team?.id"` at
    that host instead would also change what the private arm's TREE is scoped to (`tenantId` is handed
    straight to `app-side-bar-notebooks-tree` below), which is the side effect that ruled the
    symmetric candidate out in the first place.
  -->
  <app-sidebar-item
    [title]="title"
    icon="sapphire-notebook"
    class="rounded hover:bg-hover"
    [depth]="depth ?? 0"
    [expandable]="true"
    [expanded]="expanded | lsValue : application : team?.id"
    [minimized]="minimized"
    [active]="currentTeamId === (tenantId ?? team?.id) && sidebarFilter === 'all_notebooks'"
    [actions]="
      rail
        ? []
        : [{
            icon: 'sapphire-plus',
            template: notebookQuickFormTemplate,
            until: quickFormPopoverClose,
            appearance: 'dialog'
          }]
    "
    (onExpand)="expanded.set($event, application, team?.id)"
    (appClick)="selectFilter.emit('all_notebooks')"
    appSidebarContextMenu="all_notebooks"
    [appSidebarContextMenuTenantId]="tenantId"
    [appSidebarContextMenuRail]="rail"
    [appSidebarContextMenuRailScope]="railScope"
    >
    @if (rail) {
      <!--
        Rail sections only: the plus and the options menu as real buttons, so a keyboard reaches them. They show
        on hover and on focus, like the row's other controls. The options button opens the very menu a right-click
        on the header does.
      -->
      <button
        type="button"
        class="inline-flex border-0 bg-transparent p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
        [attr.aria-label]="'New Notebook in ' + railWorkspace + ' Workspace'"
        [attr.title]="'New Notebook in ' + railWorkspace + ' Workspace'"
        stchPopover
        [stchPopoverArrow]="true"
        [stchPopoverContent]="notebookQuickFormTemplate"
        stchPopoverAppearance="dialog"
        stchPopoverPlacement="right"
        stchPopoverTrigger="click"
        [stchPopoverShowUntil]="quickFormPopoverClose"
        (click)="$event.stopPropagation()"
      >
        <app-icon name="sapphire-plus" />
      </button>
      <button
        type="button"
        class="inline-flex border-0 bg-transparent p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
        [attr.aria-label]="railWorkspace + ' Notebooks options'"
        [attr.title]="railWorkspace + ' Notebooks options'"
        aria-haspopup="menu"
        appSidebarContextMenu="all_notebooks"
        [appSidebarContextMenuTenantId]="tenantId"
        [appSidebarContextMenuRail]="true"
        [appSidebarContextMenuRailScope]="railScope"
        stchPopoverTrigger="click"
        stchPopoverPlacement="bottomLeft"
        (click)="$event.stopPropagation()"
      >
        <app-icon name="sapphire-ellipsis" />
      </button>
    }
    <app-context-menu-state-indicator
      appearance="sapphire"
      svgIcon="sapphire-ellipsis"
      appNotebooksListContextMenu
      />
    </app-sidebar-item>
  </div>

  @if (!minimized) {
    <!--
      `tree-collapsed` is the only structural signal a stylesheet has for "this tree shows no rows"
      (#1147 ROW 3): the collapsed state is an animation STATE, which Angular writes as inline
      `height: 0`, and CSS cannot ask about that. The rail's divider rule needs it to know whether the
      row flush above the divider is this block's container row or the tree's last notebook.
    -->
    <div
      class="collapseable-body"
      [class.tree-collapsed]="!(expanded | lsValue : application : team?.id)"
      [@heightAnimation]="(expanded | lsValue : application : team?.id) ? 'show' : 'hide'"
      >
      <ng-container *ngTemplateOutlet="contentTemplate" />
    </div>
  }

  <ng-template #popoverTemplate>
    @if (minimized) {
      <div
        class="popover-body"
        >
        <ng-container *ngTemplateOutlet="contentTemplate" />
      </div>
    }
  </ng-template>

  <ng-template #contentTemplate>
    <app-side-bar-notebooks-tree
      [filters]="filters"
      [team]="team"
      [tenantId]="tenantId"
      [itemCreateFormTemplate]="notebookQuickFormTemplate"
    />
  </ng-template>

  <ng-template #notebookQuickFormTemplate let-parentId="parentId">
    <div class="quick-form-container">
      <app-notebook-quick-form
        [autofocus]="true"
        [parentId]="parentId"
        [tenantItems]="[{ accountId: tenantId }]"
        [inline]="true"
        [showMore]="true"
        [showCancel]="true"
        (close)="quickFormPopoverClose.next()"
        (more)="handleMore($event)"
        />
      </div>
    </ng-template>
